import type { BusinessDetail, ReviewItem } from "./types";

export type SubmittedValue = { label: string; value: string };

const text = (v: unknown): string | null =>
  typeof v === "string" && v.trim() ? v.trim() : null;

/** The company-record answer core stored for the CIN, when the reader may see it. */
function cinRecord(detail: BusinessDetail): Record<string, unknown> | null {
  const check = [...(detail.verificationChecks ?? [])]
    .reverse()
    .find((c) => c.type === "cin" && c.status === "passed");
  return check?.result?.response ?? null;
}

function address(o: {
  line1: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
}): string {
  return [o.line1, o.city, o.state, o.postalCode].filter(Boolean).join(", ");
}

/** Provider's registered office, in the keys core reads (cinRegisteredOffice). */
function recordOffice(raw: Record<string, unknown> | null): string | null {
  const office = (raw?.registered_office ?? raw?.registered_address) as
    | Record<string, unknown>
    | undefined;
  if (!office) return null;
  return (
    [office.address_line_1, office.address_line_2, office.city, office.state, office.pincode]
      .map(text)
      .filter(Boolean)
      .join(", ") || null
  );
}

/**
 * What the business submitted for one review item (the company record is read
 * once per detail, not once per item), read from the detail core
 * returns. Items with nothing but a file (MoA, AoA) return no values.
 */
export function submittedValuesFor(
  detail: BusinessDetail,
): (item: ReviewItem) => SubmittedValue[] {
  const raw = cinRecord(detail);
  return (item) => valuesOf(detail, item, raw);
}

function valuesOf(
  detail: BusinessDetail,
  item: ReviewItem,
  raw: Record<string, unknown> | null,
): SubmittedValue[] {
  const out: SubmittedValue[] = [];
  const add = (label: string, value: string | null | undefined) => {
    if (value) out.push({ label, value });
  };

  switch (item.itemKey) {
    case "pan":
      add("PAN", detail.pan);
      add("Name", detail.legalName);
      break;
    case "gst_certificate":
    case "gstin":
      add(
        "GSTIN",
        detail.offices
          .map((o) => o.gstin)
          .filter((g): g is string => Boolean(g))
          .join(", "),
      );
      break;
    case "cin_certificate":
      add("CIN", detail.cin);
      break;
    case "company_record":
      add("Name", text(raw?.company_name) ?? detail.legalName);
      add("CIN", detail.cin);
      add("Status", text(raw?.cin_status));
      add("Incorporated", text(raw?.incorporation_date));
      add(
        "Registered office",
        ((o) => (o ? address(o) : recordOffice(raw)))(
          (detail.offices ?? []).find((x) => x.isRegisteredOffice),
        ),
      );
      break;
    case "directors":
      add(
        "Directors",
        (detail.people ?? [])
          .filter((p) => p.din)
          .map((p) => `${p.name} (DIN ${p.din})`)
          .join(", "),
      );
      break;
    case "principal_place": {
      const registered = (detail.offices ?? []).find((o) => o.isRegisteredOffice);
      const main = (detail.offices ?? []).find((o) => o.isMainOffice) ?? registered;
      add("Address", main ? address(main) : recordOffice(raw));
      break;
    }
    case "bank_account":
      if (detail.bankAccount) {
        add("Account", `ending ${detail.bankAccount.last4}`);
        add("IFSC", detail.bankAccount.ifsc);
      }
      break;
    default:
      break;
  }
  return out;
}
